// src/pages/PatientAppointmentsPage.jsx
// Route: /patient/appointments
// Shows the patient's upcoming and past appointments.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import PageHead from '../components/PageHead';
import AppointmentsTabs from '../components/AppointmentsTabs';
import EmptyState from '../components/EmptyState';
import CardLink from '../components/CardLink';
import Icon from '../components/Icon';
import avatarTint from '../utils/avatarTint';
import { usePatient } from '../context/PatientContext';
import { appointmentAPI, followUpAPI, healthJourneyAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// "9:00 AM" -> "09:00". formattedTime is rendered from a plain (already-IST)
// LocalTime on the backend, so this is just an AM/PM parse, no timezone math.
function formattedTimeTo24h(formatted) {
  const [time, period] = formatted.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ─── "Confirm your follow-up" — books the tentative Health Journey milestone
// directly with the patient's own assigned Navigator. A dedicated action,
// not the general "+ Book appointment" doctor-booking flow above/below it.
function ConfirmFollowUpCard({ patientId, milestone, onConfirmed }) {
  const [expanded, setExpanded]         = useState(false);
  // "Later" is a per-visit dismiss, not a persisted snooze — same convention
  // as ChatPage's onboarding nudge banner. There's no dedicated "snooze this
  // milestone" endpoint, and the milestone is still genuinely tentative next
  // time the patient opens this page, so it reappears then rather than being
  // silently forgotten.
  const [dismissed, setDismissed]       = useState(false);
  const [date, setDate]                 = useState('');
  const [slots, setSlots]               = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null); // "HH:mm"
  const [confirming, setConfirming]     = useState(false);
  const [error, setError]               = useState('');

  if (dismissed) return null;

  function loadSlots(d) {
    setSlots([]);
    setSelectedTime(null);
    setError('');
    if (!d) return;
    setLoadingSlots(true);
    followUpAPI.availableSlots(patientId, d)
      .then(r => setSlots(r.data?.slots || []))
      .catch(() => setError('Could not load available times for that date.'))
      .finally(() => setLoadingSlots(false));
  }

  function handleDateChange(e) {
    const d = e.target.value;
    setDate(d);
    loadSlots(d);
  }

  async function handleConfirm() {
    if (!selectedTime) return;
    setConfirming(true);
    setError('');
    try {
      await followUpAPI.confirm(patientId, { date, time: selectedTime });
      onConfirmed();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not confirm this time — please try another.');
    } finally {
      setConfirming(false);
    }
  }

  const minDate = new Date().toISOString().slice(0, 10);
  const milestoneDate = milestone.date
    ? new Date(milestone.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <section style={{ marginBottom: 36 }}>
      {/* borderLeft, not borderColor: an all-around accent border reads as
          "this card is selected/active"; a left accent stripe (this is the
          one card on the page you're being nudged to act on) is what the
          Figma reference actually shows. */}
      <div className="card" style={{ margin: 0, borderLeft: '3px solid var(--brand-accent)' }}>
        <div className="appt-card-row">
          <div className="avatar avatar--t3" aria-hidden="true">
            <Icon name="calendar" size={18} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            {/* Badge sits next to the (short, single-line) title, not as a
                sibling of this whole multi-line block — a badge sharing a
                wrapped flex line with a tall text column was stretching to
                the full card width instead of sizing to its own content. */}
            <div className="appt-title-row">
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
                {milestone.label || 'Follow-up planned'}
              </div>
              <span className="badge badge-unknown" style={{ flexShrink: 0 }}>Tentative</span>
            </div>
            {milestoneDate && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3,
                fontSize: 12.5, color: 'var(--text-secondary)',
              }}>
                <Icon name="calendar" size={12} /> {milestoneDate}
              </div>
            )}
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
              {milestone.detail || 'Book a time with your health navigator to stay on track.'}
            </div>
          </div>
        </div>

        {!expanded && (
          <div className="appt-buttons-row" style={{ marginTop: 16 }}>
            <button className="btn btn-accent" onClick={() => setExpanded(true)}>Choose a time</button>
            <button className="btn btn-ghost" onClick={() => setDismissed(true)}>Later</button>
          </div>
        )}

        {expanded && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--bg-subtle)' }}>
            <div className="form-group" style={{ maxWidth: 220 }}>
              <label className="form-label">Date</label>
              <input type="date" className="form-input" min={minDate} value={date} onChange={handleDateChange} />
            </div>

            {loadingSlots ? (
              <div className="loading-state" style={{ padding: '12px 0', justifyContent: 'flex-start' }}>
                <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                Loading times…
              </div>
            ) : date && slots.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
                No times available on this date. Try another date.
              </div>
            ) : slots.length > 0 ? (
              <div className="slot-grid">
                {slots.map(slot => {
                  const time24 = formattedTimeTo24h(slot.formattedTime);
                  const isSel = selectedTime === time24;
                  return (
                    <div key={slot.startTime}
                      className={`slot ${!slot.available ? 'taken' : isSel ? 'selected' : 'available'}`}
                      onClick={() => { if (slot.available) setSelectedTime(time24); }}
                    >
                      {slot.formattedTime}{isSel ? ' ✓' : ''}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {error && <div className="alert alert-error" role="alert" style={{ marginTop: 12 }}>{error}</div>}

            <div className="appt-buttons-row" style={{ marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => setExpanded(false)} disabled={confirming}>
                Cancel
              </button>
              <button className="btn btn-accent" onClick={handleConfirm} disabled={!selectedTime || confirming}>
                {confirming ? 'Confirming…' : 'Confirm follow-up'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// Shared .badge-* classes (index.css), not hardcoded hex — same convention
// as every other status pill in the app (Care Team, doctor portal, etc.).
const STATUS_BADGES = {
  SCHEDULED: { cls: 'badge-confirmed', label: 'Scheduled' },
  COMPLETED: { cls: 'badge-active',    label: 'Completed' },
  CANCELLED: { cls: 'badge-inactive',  label: 'Cancelled' },
  PENDING:   { cls: 'badge-pending',   label: 'Pending' },
};

function statusBadge(status) {
  return STATUS_BADGES[status] ?? { cls: 'badge-unknown', label: status };
}

// Eyebrow label + a colored count pill — matches the Figma reference's
// "UPCOMING  0" / "CANCELLED  2" / "PAST  4" section headers.
function SectionHeading({ label, count, badgeClass }) {
  return (
    <h2 style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 12,
    }}>
      {label}
      <span className={`badge ${badgeClass}`}>{count}</span>
    </h2>
  );
}

function AppointmentCard({ appt, onCancel, patientId, disableCancel = false }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const badge = statusBadge(appt.status);
  const isCancelled = appt.status === 'CANCELLED';
  const name = appt.doctorName ?? (appt.navigatorName ? `${appt.navigatorName} (Navigator)` : '—');
  const dt = appt.appointmentDate
    ? new Date(appt.appointmentDate).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : '—';

  async function handleDownloadInvoice() {
    if (!patientId || downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      const res = await appointmentAPI.downloadInvoice(patientId, appt.id);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not generate an invoice for this appointment.');
    } finally {
      setDownloadingInvoice(false);
    }
  }

  return (
    // height: 100% — the Past carousel's 2-up wrapper (PastAppointmentsCarousel)
    // is a flex row with no alignItems override, so it already defaults to
    // stretch: each card's OUTER slot matches the row's tallest sibling.
    // This inner .card div didn't fill that stretched slot, though — it sized
    // to its own content, so a card whose type badge wrapped to a second
    // line (e.g. "Consultation") stayed visibly shorter than a sibling whose
    // one-word badge ("Review") fit on one line, leaving the row's card
    // borders/backgrounds at two different heights instead of the aligned
    // grid a stretched row is supposed to produce.
    <div className="card" style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
      <div className="appt-card-row">
        <div className={`avatar ${avatarTint(name)}`} aria-hidden="true" style={{ opacity: isCancelled ? 0.5 : 1, flexShrink: 0 }}>
          {initials(name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* Badge sits next to the (short, single-line) name, not as a
              sibling of this whole multi-line block — see the identical
              note on ConfirmFollowUpCard's title/badge row above. */}
          <div className="appt-title-row">
            {/* minWidth: 0 + ellipsis, not the default wrap-at-word-boundary
                — the Past carousel's 2-up cards are narrow enough that a
                longer doctor name wrapped onto its own second line, and
                still left the status badge beside it too little room and
                breaking mid-word (the shared .badge's overflow-wrap: anywhere
                rule, meant for genuinely long badge text, not a squeeze this
                row itself caused). Truncating the name instead keeps both on
                one line, same as any other single-line title. */}
            <div style={{
              fontWeight: 700, fontSize: 15, color: 'var(--text-primary)',
              textDecoration: isCancelled ? 'line-through' : 'none',
              opacity: isCancelled ? 0.65 : 1,
              minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {name}
            </div>
            <span className={`badge ${badge.cls}`} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{badge.label}</span>
          </div>
          <div className="appt-meta-row" style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name="calendar" size={12} /> {dt}
            </span>
            {appt.appointmentTime && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon name="clock" size={12} /> {appt.appointmentTime}
              </span>
            )}
          </div>
          {appt.appointmentType && (
            // No display/whiteSpace override — this badge's actual value
            // ("NEW_CONSULTATION", "FOLLOW_UP") is longer than the other
            // short, single-word badges on this card that legitimately want
            // nowrap, and at the Past carousel's half-width cards it doesn't
            // fit on one line. Forcing nowrap here defeated .badge's own
            // "overflow guard" (index.css — max-width: 100% + overflow-wrap:
            // anywhere, built for exactly this "long value in a badge" case)
            // instead of using it, so the text spilled out past the card's
            // edge/corner instead of wrapping inside it.
            <span className="badge badge-confirmed" style={{ marginTop: 8 }}>
              {appt.appointmentType}
            </span>
          )}
        </div>
      </div>

      {((appt.status === 'SCHEDULED' && !disableCancel) || appt.status === 'COMPLETED') && (
        <div className="appt-actions-row">
          {appt.status === 'SCHEDULED' && !disableCancel && (
            /* .btn-ghost, not .btn-secondary: .btn-secondary is
               var(--brand-dark) text/border with no dark-theme override, so
               it renders near-black-on-near-black in dark theme (the same
               shape of bug --text-on-brand-dark exists for elsewhere in the
               app). .btn-ghost uses theme-aware --text-secondary/--border. */
            <button className="btn btn-ghost" type="button" onClick={() => onCancel(appt.id)}>
              Cancel
            </button>
          )}
          {appt.status === 'COMPLETED' && (
            <>
              <button
                className="btn btn-accent"
                type="button"
                onClick={() => navigate('/patient/appointments/book')}
              >
                Book follow-up
              </button>
              {/* No per-appointment visit lookup yet (the patient visit list
                  doesn't carry appointmentId), so this opens the visit
                  summaries list rather than a single visit. */}
              <CardLink to="/patient/visits">View notes</CardLink>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
              >
                {downloadingInvoice ? 'Generating…' : 'Invoice'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// "09:30" / "09:30:00" -> "9:30 AM". The backend sends a plain LocalTime, so
// this is formatting only, no timezone math.
function formatTime12(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return String(t);
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// Local-time Date for an appointment (date + time), used for sorting and the
// "In N days" hint.
function apptStart(a) {
  const [y, mo, d] = String(a.appointmentDate).slice(0, 10).split('-').map(Number);
  const [h = 0, m = 0] = String(a.appointmentTime || '00:00').split(':').map(Number);
  return new Date(y, mo - 1, d, h, m);
}

function daysUntil(date) {
  const startOfDay = x => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const n = Math.round((startOfDay(date) - startOfDay(new Date())) / 86400000);
  if (n <= 0) return 'Today';
  return n === 1 ? 'Tomorrow' : `In ${n} days`;
}

const pad2 = n => String(n).padStart(2, '0');
const icsStamp = d => `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`
  + `T${pad2(d.getHours())}${pad2(d.getMinutes())}00`;

// The API has no duration field, so the calendar entry assumes a 30-minute slot.
function downloadIcs(appt, title) {
  const start = apptStart(appt);
  const end = new Date(start.getTime() + 30 * 60000);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Previu//Appointments//EN',
    'BEGIN:VEVENT',
    `UID:${appt.id}@previu`,
    `DTSTAMP:${icsStamp(new Date())}`,
    `DTSTART:${icsStamp(start)}`,
    `DTEND:${icsStamp(end)}`,
    `SUMMARY:${title}`,
    appt.location ? `LOCATION:${appt.location}` : null,
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean);
  const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/calendar' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'previu-appointment.ics';
  link.click();
  URL.revokeObjectURL(url);
}

function NextAppointmentCard({ appt, onCancel }) {
  const navigate = useNavigate();
  const start = apptStart(appt);
  const isDoctor = !!appt.doctorName;
  const name = appt.doctorName ?? appt.navigatorName ?? '—';
  const role = isDoctor ? (appt.doctorSpecialty || 'Your doctor') : 'Your health navigator';
  const isVideo = appt.consultationMode === 'TELEMEDICINE';
  const confirmed = appt.status === 'CONFIRMED' || appt.status === 'SCHEDULED';

  return (
    <section style={{ marginBottom: 36 }}>
      <div className="appt-section-head">
        <h2 className="appt-section-title">
          Next appointment <span className="appt-count">1</span>
        </h2>
        <span className="appt-section-hint">{daysUntil(start)}</span>
      </div>
      <div className="appt-hero" style={{ marginBottom: 0 }}>
        <div className="appt-hero__date" aria-hidden="true">
          <span className="appt-hero__month">{start.toLocaleDateString('en-IN', { month: 'short' })}</span>
          <span className="appt-hero__day">{start.getDate()}</span>
          <span className="appt-hero__dow">{start.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
        </div>
        <div className="appt-hero__main">
          <div className="appt-hero__body">
            <span className="appt-hero__status">{confirmed ? 'Confirmed' : 'Pending'}</span>
            <h3 className="appt-hero__name">{name}</h3>
            <div className="appt-hero__role">{role}</div>

            <div className="appt-hero__facts">
              <div className="appt-fact">
                <Icon name="clock" size={20} />
                <div>
                  <div className="appt-fact__label">{formatTime12(appt.appointmentTime)}</div>
                </div>
              </div>
              <div className="appt-fact">
                <Icon name={isVideo ? 'video' : 'pin'} size={20} />
                <div>
                  <div className="appt-fact__label">{isVideo ? 'Video consultation' : 'In-person visit'}</div>
                  <div className="appt-fact__sub">
                    {isVideo ? 'Join from anywhere' : (appt.location || 'Location shared by your care team')}
                  </div>
                </div>
              </div>
            </div>

            <div className="appt-hero__actions">
              <button className="btn btn-accent" type="button"
                onClick={() => downloadIcs(appt, `Appointment with ${name}`)}>
                <Icon name="calendar" size={16} /> Add to calendar
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => navigate('/patient/care-team')}>
                <Icon name="chat" size={16} /> Message {isDoctor ? 'care team' : 'navigator'}
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => onCancel(appt.id)}
                style={{ border: 'none' }}>
                Cancel
              </button>
            </div>
          </div>
          <div className="appt-hero__footer">
            <Icon name="check" size={16} />
            Add it to your calendar so you don't miss it.
          </div>
        </div>
      </div>
    </section>
  );
}

const HISTORY_FILTERS = [
  { value: 'ALL',       label: 'All appointments' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];
const HISTORY_PAGE = 4;

function HistoryCard({ appt, patientId }) {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const isCancelled = appt.status === 'CANCELLED';
  const name = appt.doctorName ?? appt.navigatorName ?? '—';
  const type = appt.appointmentType
    ? appt.appointmentType.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase())
    : (appt.doctorName ? 'Consultation' : 'Navigator check-in');
  const date = appt.appointmentDate
    ? new Date(appt.appointmentDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  async function handleInvoice() {
    if (!patientId || downloading) return;
    setDownloading(true);
    try {
      const res = await appointmentAPI.downloadInvoice(patientId, appt.id);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not generate an invoice for this appointment.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="appt-history-card">
      <div className="appt-history-card__top">
        <div className={`avatar ${avatarTint(name)}`} aria-hidden="true" style={{ flexShrink: 0 }}>
          {initials(name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="appt-history-card__name">{name}</div>
          <div className="appt-history-card__type">{type}</div>
        </div>
        {isCancelled && <span className="badge badge-inactive" style={{ flexShrink: 0 }}>Cancelled</span>}
      </div>
      <div className="appt-history-card__when">
        <Icon name="calendar" size={14} /> {date}
        {appt.appointmentTime && <>
          <span aria-hidden="true">·</span> {formatTime12(appt.appointmentTime)}
        </>}
      </div>
      {!isCancelled && (
        <div className="appt-history-card__links">
          {/* The patient visit list doesn't carry appointmentId yet, so we
              can't deep-link to one visit; the visit summaries list is the
              page that actually holds the summary. */}
          <CardLink to="/patient/visits">View appointment summary</CardLink>
          {appt.status === 'COMPLETED' && (
            <button type="button" className="appt-link" onClick={handleInvoice} disabled={downloading}>
              {downloading ? 'Generating…' : 'Invoice'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AppointmentHistory({ items, patientId }) {
  const [filter, setFilter]     = useState('ALL');
  const [expanded, setExpanded] = useState(false);
  const filtered = items.filter(a => filter === 'ALL'
    || (filter === 'CANCELLED' ? a.status === 'CANCELLED' : a.status !== 'CANCELLED'));
  const shown = expanded ? filtered : filtered.slice(0, HISTORY_PAGE);

  return (
    <section>
      <div className="appt-section-head" style={{ alignItems: 'flex-end' }}>
        <div>
          <h2 className="appt-section-title appt-section-title--display">
            Appointment history <span className="appt-count appt-count--muted">{items.length}</span>
          </h2>
          <p className="appt-section-sub">Access visit details and summaries from previous consultations.</p>
        </div>
        <select className="form-input" style={{ width: 'auto' }} aria-label="Filter appointment history"
          value={filter} onChange={e => { setFilter(e.target.value); setExpanded(false); }}>
          {HISTORY_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      {filtered.length === 0 ? (
        <EmptyState framed icon="calendar" title="No appointments here" message="Nothing matches this filter." />
      ) : (
        <div className="appt-history-grid">
          {shown.map(a => <HistoryCard key={a.id} appt={a} patientId={patientId} />)}
        </div>
      )}
      {filtered.length > HISTORY_PAGE && (
        <div className="appt-more">
          <button type="button" className="appt-link" onClick={() => setExpanded(v => !v)}>
            {expanded ? 'Show fewer' : `View all ${filtered.length} appointments`}
            <span aria-hidden="true" style={{ display: 'inline-flex', transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)' }}>
              <Icon name="chevronRight" size={14} />
            </span>
          </button>
        </div>
      )}
    </section>
  );
}

// `only` — 'navigator' scopes this same page to appointments with a
// navigator (follow-up check-ins), mounted at its own route
// (/patient/appointments/navigator, see App.js) rather than duplicating
// this whole page for a second appointment kind. Omitted (the default
// /patient/appointments mount) keeps every appointment type mixed, exactly
// as this page always has — see AppointmentsTabs.jsx's own comment on why
// that stays unfiltered instead of becoming "doctor-only" now that a
// dedicated navigator view exists.
export default function PatientAppointmentsPage({ only } = {}) {
  const { patient } = usePatient();
  const navigate    = useNavigate();
  const isNavigatorOnly = only === 'navigator';
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  // Health Journey's nextMilestone — only used here to detect the tentative
  // "Follow-up planned" case (no confirmed appointment yet) and show the
  // "Confirm your follow-up" card; null while loading or if there's none.
  const [nextMilestone, setNextMilestone] = useState(null);
  // Inline confirmation — id of appointment pending cancel, null when none
  const [confirmId, setConfirmId]       = useState(null);
  const confirmAppt = appointments.find(a => a.id === confirmId);

  function reloadAppointments() {
    if (!patient?.id) return;
    appointmentAPI.listByPatient(patient.id)
      .then(res => setAppointments(res.data ?? []))
      .catch(() => setError('Could not load appointments.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reloadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  useEffect(() => {
    if (!patient?.id) return;
    healthJourneyAPI.get(patient.id)
      .then(res => setNextMilestone(res.data?.nextMilestone ?? null))
      .catch(() => {}); // non-critical — the page still works without this card
  }, [patient?.id]);

  function handleFollowUpConfirmed() {
    setNextMilestone(null); // the tentative milestone resolves once a real appointment exists
    reloadAppointments();
  }

  async function handleCancel(id) {
    setConfirmId(id);
  }

  async function confirmCancel() {
    const id = confirmId;
    setConfirmId(null);
    try {
      await appointmentAPI.cancel(id);
      setAppointments(prev =>
        prev.map(a => a.id === id ? { ...a, status: 'CANCELLED' } : a)
      );
    } catch {
      setError('Could not cancel appointment. Please try again.');
    }
  }

  // DEF-017 — "Cancelled future appointment appears under Past section."
  //
  // Bucketing used to be derived here from STATUS ALONE: SCHEDULED/PENDING were
  // upcoming and everything else fell through to past. A cancelled appointment
  // NEXT WEEK is not in the past, and calling it that told the patient something
  // untrue about their own record. The doctor portal made the mirror-image
  // mistake with a date-only rule and counted the same appointment as Upcoming
  // (DEF-025) — two views, two rules, two different wrong answers.
  //
  // The server now computes this once and sends it as `bucket`
  // (UPCOMING | CANCELLED | PAST — see AppointmentBucket on the backend), so
  // every view agrees. Falling back to the old status test keeps this page
  // working against an API that predates the field, rather than showing an empty
  // list if it is missing.
  const bucketOf = (a) => a.bucket
    || (a.status === 'CANCELLED' ? 'CANCELLED'
      : (a.status === 'SCHEDULED' || a.status === 'PENDING' || a.status === 'CONFIRMED')
        ? 'UPCOMING'
        : 'PAST');

  // doctorName/navigatorName are mutually exclusive per AppointmentDTO (see
  // HealthJourneyService's own providerDetail for the same rule), so a
  // navigator appointment is exactly "has no doctorName".
  // The default (Doctor) tab is the mirror image: navigator check-ins live on
  // their own tab, so they are not listed here.
  const scoped = appointments.filter(a => isNavigatorOnly ? !a.doctorName : !!a.doctorName);

  const byStart = (x, y) => apptStart(x) - apptStart(y);
  const upcomingSorted = scoped.filter(a => bucketOf(a) === 'UPCOMING').sort(byStart);
  // Cancelled visits sit in the history alongside past ones (newest first);
  // the history filter separates them again.
  const history = scoped.filter(a => bucketOf(a) !== 'UPCOMING').sort((x, y) => byStart(y, x));

  return (
    <PatientAppShell breadcrumbs={[
      { label: 'My Appointments', to: '/patient/appointments' },
      { label: isNavigatorOnly ? 'Navigator' : 'Appointments' },
    ]}>
      {/* index.css has a handful of old, broad mobile rules — originally for
          one dashboard's "quick actions" cards — that match ANY div inside
          .card whose inline style attribute happens to contain the literal
          substring "flex" (direct children) or both "display: flex" and
          "gap" (any depth), and force flex-direction: column !important on
          it. That's a coincidence of string-matching, not something aimed
          at this page, but every flex row below lives inside a .card and
          would otherwise get silently forced into a vertical stack on
          mobile — these classes exist so the layout is driven by a
          stylesheet rule this file owns, not an inline style attribute
          those old selectors can pattern-match against.

          .appt-page .btn is scoped to this page's own content wrapper
          (below), not a bare ".btn" rule — .btn is the shared button class
          every portal uses, and this page's own <style> tag is otherwise
          unscoped global CSS for as long as the page is mounted. The Figma
          reference's buttons ("+ Book appointment", "Choose a time" /
          "Later", "Cancel" / "Book follow-up" / "Invoice") are all fully
          rounded pills, not the shared class's default var(--radius-sm)
          rounded-rect — scoping the override here matches this screen
          without reshaping every button in the app. */}
      <style>{`
        .appt-page .btn { border-radius: 999px; }
        .appt-card-row { display: flex; align-items: flex-start; gap: 14px; }
        /* flex-wrap: wrap — at the Past carousel's half-width cards, a
           longer status badge ("SCHEDULED") left almost no room beside it
           on one line, so the name's own ellipsis (needed so a squeeze
           truncates rather than breaks mid-word — see AppointmentCard's
           title div) shrank it down to a sliver, sometimes rendering as
           visually empty. Wrapping lets the badge drop to its own line
           instead of fighting the name for space on one. */
        .appt-title-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
        /* flex-wrap: wrap — same reasoning as .appt-title-row above, one row
           down: at the Past carousel's half-width cards there isn't room for
           the date AND the time side by side, and with no wrap the row held
           its ground and the time simply overflowed past the card's own
           edge instead of dropping to its own line. Each span below also
           gets white-space: nowrap, so a date like "19 Sept 2026" wraps here
           as one whole unit rather than breaking word-by-word inside itself
           when it's squeezed for space. */
        .appt-meta-row { display: flex; align-items: center; gap: 4px 12px; flex-wrap: wrap; }
        .appt-meta-row > span { white-space: nowrap; }
        .appt-buttons-row { display: flex; gap: 10px; }
        .appt-actions-row { display: flex; align-items: center; justify-content: flex-start; gap: 10px; flex-wrap: wrap; }
      `}</style>

      {/* ── Inline cancel confirmation modal ── */}
      {confirmId && confirmAppt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(13,30,53,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
            padding: '28px 24px', width: '100%', maxWidth: 380,
            boxShadow: 'var(--shadow-lg)', fontFamily: 'var(--font-body)',
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)',
              fontFamily: 'var(--font-display)', marginBottom: 8 }}>
              Cancel appointment?
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
              Your appointment with{' '}
              <strong>{confirmAppt.doctorName ?? confirmAppt.navigatorName ?? '—'}</strong>
              {confirmAppt.appointmentDate && (
                <> on <strong>{new Date(confirmAppt.appointmentDate)
                  .toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' })}</strong></>
              )} will be cancelled. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmId(null)}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)', background: 'var(--bg-subtle)',
                  fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  fontFamily: 'var(--font-body)', color: 'var(--text-secondary)',
                }}
              >
                Keep appointment
              </button>
              <button
                onClick={confirmCancel}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 'var(--radius-md)',
                  border: 'none', background: 'var(--red)',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'var(--font-body)', color: 'var(--text-on-brand-dark)',
                }}
              >
                Yes, cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* .page-shell so the heading lines up with My Documents and My Care
          Plan — this page ran full bleed. The cancel modal above is
          position:fixed and deliberately stays outside the column. */}
      <div className="page-shell appt-page appt-page--wide">
      <AppointmentsTabs />
      <PageHead
        eyebrow={isNavigatorOnly ? 'Your care team' : 'Your health journey'}
        title={isNavigatorOnly ? 'Navigator check-ins' : 'My appointments'}
        subtitle={isNavigatorOnly
          ? 'Follow-up check-ins with your assigned navigator.'
          : 'Everything you need for your upcoming and previous visits.'}
        // No self-service booking flow for a navigator visit the way there
        // is for a doctor visit (/patient/appointments/book) — the only way
        // one gets on this list is the "Confirm your follow-up" card below,
        // when the Health Journey has a tentative one ready.
        actions={isNavigatorOnly ? null : (
          <button className="btn btn-accent" onClick={() => navigate('/patient/appointments/book')}>
            + Book appointment
          </button>
        )}
      />

      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          Loading appointments…
        </div>
      )}

      {error && (
        <div className="alert alert-error" role="alert">{error}</div>
      )}

      {!loading && !error && (
        <>
          {nextMilestone?.tentative && patient?.id && (
            <ConfirmFollowUpCard
              patientId={patient.id}
              milestone={nextMilestone}
              onConfirmed={handleFollowUpConfirmed}
            />
          )}

          {/* Upcoming — the soonest visit gets the hero card; any others
              follow as compact cards. */}
          {upcomingSorted.length === 0 ? (
            <section style={{ marginBottom: 36 }}>
              <SectionHeading label="Upcoming" count={0} badgeClass="badge-active" />
              <EmptyState
                framed
                hero
                icon="calendar"
                title="Nothing scheduled"
                message={isNavigatorOnly
                  ? 'Your navigator schedules these directly with you. If a follow-up '
                    + 'is ready to confirm, you\'ll see it above.'
                  : 'Book a consultation when you\u2019re ready. Your doctor will review your '
                    + 'reports and confirmed visits will appear here with joining details.'}
                action={isNavigatorOnly ? undefined
                  : { label: 'Book an appointment', icon: 'plus', onClick: () => navigate('/patient/appointments/book') }}
                hint={isNavigatorOnly ? undefined : 'Choose a time that works for you'}
                hintIcon="check"
              />
            </section>
          ) : (
            <>
              <NextAppointmentCard appt={upcomingSorted[0]} onCancel={handleCancel} />
              {upcomingSorted.length > 1 && (
                <section style={{ marginBottom: 36 }}>
                  <SectionHeading label="Also upcoming" count={upcomingSorted.length - 1} badgeClass="badge-active" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {upcomingSorted.slice(1).map(a => (
                      <AppointmentCard key={a.id} appt={a} onCancel={handleCancel} patientId={patient?.id} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {history.length > 0 && <AppointmentHistory items={history} patientId={patient?.id} />}
        </>
      )}
      </div>
    </PatientAppShell>
  );
}

